import { Component, OnInit } from '@angular/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import * as alertifyjs from 'alertifyjs';

const VALID_EVAL_MODES = ['BOTH', 'EITHER', 'SCORE_ONLY', 'PROBABILITY_ONLY'] as const;
type EvaluationMode = typeof VALID_EVAL_MODES[number];

type ScoreModel = 'score' | 'score_v2';

interface ModelThresholds {
    minScore: number | null;
    maxProbability: number | null;
    inherited: boolean;
    effectiveMinScore: number | null;
    effectiveMaxProbability: number | null;
}

interface CredolabConfigData {
    minScore: number | null;
    maxProbability: number | null;
    requireBoth: boolean | null;
    evaluationMode: EvaluationMode | null;
    // Interruptor: qué modelo decide. null = automático.
    scoreModel: ScoreModel | null;
    models: Record<string, ModelThresholds>;
    availableScoreModels: ScoreModel[];
    autoRefuseFlags: Record<string, boolean>;
    availableFlags: string[];
    updatedBy: string | null;
    updatedAt: string | null;
}

@Component({
    selector: 'app-credolab-thresholds',
    templateUrl: './credolab-thresholds.component.html',
    styleUrls: ['./credolab-thresholds.component.css']
})
export class CredolabThresholdsComponent implements OnInit {

    credolabData: CredolabConfigData | null = null;

    credolabLoading = false;
    credolabSaving = false;
    credolabNotSeeded = false;

    thresholdsForm = {
        minScore: null as number | null,
        maxProbability: null as number | null,
        evaluationMode: null as EvaluationMode | null,
        scoreModel: null as ScoreModel | null,
    };

    // Umbrales propios por modelo (null = hereda el global)
    modelsForm: Record<string, { minScore: number | null; maxProbability: number | null }> = {};

    // Orden y etiquetas de los modelos en pantalla
    readonly MODEL_ORDER: ScoreModel[] = ['score_v2', 'score'];
    readonly MODEL_LABELS: Record<string, string> = {
        score_v2: 'Modelo nuevo (score_v2)',
        score: 'Modelo anterior (score)',
    };

    constructor(private appConfigService: AppConfigService) {}

    ngOnInit(): void {
        this.loadCredolab();
    }

    async loadCredolab(): Promise<void> {
        this.credolabLoading = true;
        try {
            const res = await this.appConfigService.getCredolabConfig();
            this.credolabData = res?.data || res;
            this.thresholdsForm = {
                minScore: this.credolabData?.minScore ?? null,
                maxProbability: this.credolabData?.maxProbability ?? null,
                evaluationMode: this.credolabData?.evaluationMode ?? 'BOTH',
                scoreModel: this.credolabData?.scoreModel ?? null,
            };
            this.modelsForm = {};
            for (const code of this.MODEL_ORDER) {
                const m = this.credolabData?.models?.[code];
                this.modelsForm[code] = {
                    minScore: m?.minScore ?? null,
                    maxProbability: m?.maxProbability ?? null,
                };
            }
            this.credolabNotSeeded = false;
        } catch (error: any) {
            if (error?.status === 404) {
                this.credolabNotSeeded = true;
                this.credolabData = null;
            } else {
                alertifyjs.error(`Error cargando config: ${error?.error?.message || error?.message || 'desconocido'}`);
            }
        } finally {
            this.credolabLoading = false;
        }
    }

    async saveThresholds(): Promise<void> {
        const payload: any = {};
        if (this.thresholdsForm.minScore !== null && this.thresholdsForm.minScore !== this.credolabData?.minScore) payload.minScore = this.thresholdsForm.minScore;
        if (this.thresholdsForm.maxProbability !== null && this.thresholdsForm.maxProbability !== this.credolabData?.maxProbability) payload.maxProbability = this.thresholdsForm.maxProbability;
        if (this.thresholdsForm.evaluationMode !== null && this.thresholdsForm.evaluationMode !== this.credolabData?.evaluationMode) payload.evaluationMode = this.thresholdsForm.evaluationMode;
        if (this.thresholdsForm.scoreModel !== (this.credolabData?.scoreModel ?? null)) payload.scoreModel = this.thresholdsForm.scoreModel;

        // Umbrales por modelo: sólo se manda lo que cambió. null = heredar el global.
        const models: Record<string, any> = {};
        for (const code of this.MODEL_ORDER) {
            const actual = this.credolabData?.models?.[code];
            const form = this.modelsForm[code];
            if (!form) continue;
            const entry: any = {};
            if ((form.minScore ?? null) !== (actual?.minScore ?? null)) entry.minScore = form.minScore ?? null;
            if ((form.maxProbability ?? null) !== (actual?.maxProbability ?? null)) entry.maxProbability = form.maxProbability ?? null;
            if (Object.keys(entry).length > 0) models[code] = entry;
        }
        if (Object.keys(models).length > 0) payload.models = models;

        if (Object.keys(payload).length === 0) {
            alertifyjs.message('No hay cambios para guardar.');
            return;
        }

        if (payload.minScore !== undefined && (payload.minScore < 0 || payload.minScore > 1000)) {
            alertifyjs.warning('minScore debe estar entre 0 y 1000');
            return;
        }
        if (payload.maxProbability !== undefined && (payload.maxProbability < 0 || payload.maxProbability > 1)) {
            alertifyjs.warning('maxProbability debe estar entre 0 y 1');
            return;
        }
        if (payload.evaluationMode !== undefined
            && !VALID_EVAL_MODES.includes(payload.evaluationMode)) {
            alertifyjs.warning('Modo de evaluación inválido');
            return;
        }

        for (const [code, v] of Object.entries(models)) {
            if (v.minScore !== undefined && v.minScore !== null && (v.minScore < 0 || v.minScore > 1000)) {
                alertifyjs.warning(`El score mínimo de ${this.MODEL_LABELS[code] || code} debe estar entre 0 y 1000`);
                return;
            }
            if (v.maxProbability !== undefined && v.maxProbability !== null && (v.maxProbability < 0 || v.maxProbability > 1)) {
                alertifyjs.warning(`La probabilidad máxima de ${this.MODEL_LABELS[code] || code} debe estar entre 0 y 1`);
                return;
            }
        }

        const confirmed = window.confirm(
            `¿Actualizar umbrales Credolab?\n` +
            `Modelo que decide: ${this.getScoreModelLabel(this.thresholdsForm.scoreModel)}\n` +
            `minScore=${this.thresholdsForm.minScore}\n` +
            `maxProbability=${this.thresholdsForm.maxProbability}\n` +
            `Modo: ${this.getEvaluationModeLabel(this.thresholdsForm.evaluationMode)}`
        );
        if (!confirmed) return;

        this.credolabSaving = true;
        try {
            const res = await this.appConfigService.updateCredolabThresholds(payload);
            if (res?.data?.unchanged) {
                alertifyjs.message('Los valores ya eran los solicitados.');
            } else {
                alertifyjs.success('Umbrales actualizados');
            }
            await this.loadCredolab();
        } catch (error: any) {
            alertifyjs.error(error?.error?.message || error?.message || 'Error al guardar');
        } finally {
            this.credolabSaving = false;
        }
    }

    getScoreModelLabel(model: ScoreModel | null): string {
        if (!model) return 'Automático (decide el modelo anterior)';
        return this.MODEL_LABELS[model] || model;
    }

    /** Umbral efectivo de un modelo: el propio si lo tiene, si no el global. */
    getEffective(code: string, campo: 'minScore' | 'maxProbability'): number | null {
        const propio = this.modelsForm[code]?.[campo];
        if (propio !== null && propio !== undefined) return propio;
        return campo === 'minScore' ? this.thresholdsForm.minScore : this.thresholdsForm.maxProbability;
    }

    /** true si ese modelo es el que hoy decide, según el interruptor. */
    esModeloDecisivo(code: string): boolean {
        return this.thresholdsForm.scoreModel ? this.thresholdsForm.scoreModel === code : code === 'score';
    }

    getEvaluationModeLabel(mode: EvaluationMode | null): string {
        switch (mode) {
            case 'BOTH':             return 'Ambos umbrales deben pasar';
            case 'EITHER':           return 'Basta con que uno pase';
            case 'SCORE_ONLY':       return 'Solo score decide';
            case 'PROBABILITY_ONLY': return 'Solo probabilidad decide';
            default:                 return '—';
        }
    }
}
